/**
 * Minimal UTC date extension for date-fns v4.
 *
 * date-fns reads and writes calendar fields through the *local* Date accessors
 * (getHours, setFullYear, ...). A `UTCDate` routes those accessors to their UTC
 * counterparts, so passing `{ in: utc }` to `format` / `parse` / `parseISO`
 * makes them operate on UTC wall-clock time regardless of the host's time
 * zone. This mirrors `@date-fns/utc` without adding the dependency.
 *
 * Ported unchanged from the Data Mapper (frontend/src/services/utcDate.ts).
 *
 * shortcut: a COPY of packages/api/src/services/data-mapper/transform/utcDate.ts (the API's is the
 * one an import uses). The web pins date-fns 3, which has no `{ in }` option, so the web engine
 * hands format() a UTCDate INSTANCE instead: date-fns 3's toDate keeps the instance's
 * constructor, so format() reads UTC fields through the overrides below. (date-fns 3's parse()
 * does not honour it — see transformPreview.ts parseAsUtc.)
 */
const constructDateFrom = Symbol.for('constructDateFrom')

type UTCDateArgs =
  | []
  | [value: number | string | Date]
  | [
      year: number,
      monthIndex: number,
      date?: number,
      hours?: number,
      minutes?: number,
      seconds?: number,
      ms?: number
    ]

export class UTCDate extends Date {
  constructor(...args: UTCDateArgs) {
    super(UTCDate.toTime(args))
  }

  private static toTime(args: (number | string | Date | undefined)[]): number {
    if (args.length === 0) return Date.now()
    if (args.length === 1) {
      const [value] = args
      return value instanceof Date ? value.getTime() : new Date(value as number | string).getTime()
    }
    const [y, mo, d = 1, h = 0, mi = 0, s = 0, ms = 0] = args as number[]
    const utc = new Date(0)
    utc.setUTCFullYear(y, mo, d)
    utc.setUTCHours(h, mi, s, ms)
    return utc.getTime()
  }

  [constructDateFrom](value: number | string | Date): UTCDate {
    return new UTCDate(value)
  }

  override getFullYear() {
    return this.getUTCFullYear()
  }
  override getMonth() {
    return this.getUTCMonth()
  }
  override getDate() {
    return this.getUTCDate()
  }
  override getDay() {
    return this.getUTCDay()
  }
  override getHours() {
    return this.getUTCHours()
  }
  override getMinutes() {
    return this.getUTCMinutes()
  }
  override getSeconds() {
    return this.getUTCSeconds()
  }
  override getMilliseconds() {
    return this.getUTCMilliseconds()
  }
  override getTimezoneOffset() {
    return 0
  }

  override setFullYear(...args: Parameters<Date['setFullYear']>) {
    return this.setUTCFullYear(...args)
  }
  override setMonth(...args: Parameters<Date['setMonth']>) {
    return this.setUTCMonth(...args)
  }
  override setDate(...args: Parameters<Date['setDate']>) {
    return this.setUTCDate(...args)
  }
  override setHours(...args: Parameters<Date['setHours']>) {
    return this.setUTCHours(...args)
  }
  override setMinutes(...args: Parameters<Date['setMinutes']>) {
    return this.setUTCMinutes(...args)
  }
  override setSeconds(...args: Parameters<Date['setSeconds']>) {
    return this.setUTCSeconds(...args)
  }
  override setMilliseconds(...args: Parameters<Date['setMilliseconds']>) {
    return this.setUTCMilliseconds(...args)
  }
}

/** date-fns `in` context that evaluates in UTC. */
export const utc = (value: Date | number | string): UTCDate => new UTCDate(value)
